import {
  Box,
  Card,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";

/**
 * Search box + "Filter by Status" for the parent/student Billing tabs.
 *
 * This was built into the Papers tab only; the other tabs had nothing. Pulled out so every
 * tab shows the same control, in the same place, with the same look.
 */
export const PAYMENT_STATUS_OPTIONS = [
  { value: "paid", label: "Paid" },
  { value: "pending", label: "Pending" },
  { value: "failed", label: "Failed" },
  { value: "canceled", label: "Canceled" },
  { value: "expired", label: "Expired" },
  { value: "refunded", label: "Refunded" },
  { value: "partially_refunded", label: "Partially Refunded" },
];

/**
 * Normalise the many spellings the billing endpoints use ("PAID", "completed", "cancelled")
 * onto the values in PAYMENT_STATUS_OPTIONS, so one status filter works on every tab.
 */
export function normalisePaymentStatus(status) {
  const s = String(status || "").trim().toLowerCase();
  // Not "active": that is a subscription period on the App tab, not a payment.
  if (s === "completed" || s === "succeeded") return "paid";
  if (s === "cancelled") return "canceled";
  return s;
}

/** Case-insensitive "does any of these fields contain the search text". */
export function matchesSearch(term, fields) {
  const needle = String(term || "").trim().toLowerCase();
  if (!needle) return true;
  return fields.some((f) => String(f ?? "").toLowerCase().includes(needle));
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    bgcolor: "white",
    "&:hover fieldset": { borderColor: "#667eea" },
    "&.Mui-focused fieldset": { borderColor: "#667eea", borderWidth: 2 },
  },
};

export default function BillingFilterBar({
  search,
  onSearchChange,
  status,
  onStatusChange,
  placeholder = "Search...",
  statusOptions = PAYMENT_STATUS_OPTIONS,
}) {
  return (
    <Card
      sx={{
        mb: 3,
        p: 2.5,
        background:
          "linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)",
        border: `1px solid ${alpha("#667eea", 0.2)}`,
        borderRadius: 3,
        boxShadow: "0 4px 20px rgba(102, 126, 234, 0.1)",
      }}
    >
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          fullWidth
          sx={{ maxWidth: 400, ...fieldSx }}
          placeholder={placeholder}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#667eea" }} />
              </InputAdornment>
            ),
          }}
        />
        <FormControl
          sx={{
            flex: { xs: "1 1 100%", sm: "0 0 auto" },
            minWidth: { sm: 200 },
            maxWidth: { xs: "100%", sm: "none" },
            ...fieldSx,
          }}
        >
          <InputLabel>Filter by Status</InputLabel>
          <Select
            value={status}
            onChange={(e) => onStatusChange(e.target.value)}
            label="Filter by Status"
          >
            <MenuItem value="">All Status</MenuItem>
            {statusOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Card>
  );
}
